import { useEffect, useRef, useState } from 'react';
import {
  Award, BookOpen, Clock, Heart, Leaf, ShieldCheck, Sparkles, Sprout, Star,
  TrendingUp, Trophy, Users, Zap,
} from 'lucide-react';
import { statNumber } from '@/lib/stat-number';

interface StatsCounterProps {
  section: any;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  Award: <Award className="h-8 w-8" />,
  BookOpen: <BookOpen className="h-8 w-8" />,
  Clock: <Clock className="h-8 w-8" />,
  Heart: <Heart className="h-8 w-8" />,
  Leaf: <Leaf className="h-8 w-8" />,
  ShieldCheck: <ShieldCheck className="h-8 w-8" />,
  Sprout: <Sprout className="h-8 w-8" />,
  Star: <Star className="h-8 w-8" />,
  TrendingUp: <TrendingUp className="h-8 w-8" />,
  Trophy: <Trophy className="h-8 w-8" />,
  Users: <Users className="h-8 w-8" />,
  Zap: <Zap className="h-8 w-8" />,
};

function useCountUp(target: number, enabled: boolean, duration = 1500): number {
  const [value, setValue] = useState(0);
  const ref = useRef(false);

  useEffect(() => {
    if (!enabled || ref.current) return;
    ref.current = true;

    const start = performance.now();
    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out quad
      const eased = 1 - (1 - progress) * (1 - progress);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [enabled, target, duration]);

  return enabled ? value : target;
}

function StatItem({ stat, animated }: { stat: any; animated: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!animated || !containerRef.current) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [animated]);

  // Parse numeric portion for animation. Only a clean integer (with an optional
  // prefix/suffix and thousands separators) is count-up eligible; anything with
  // an embedded decimal or other digits — "99.9%", "24/7", "$1.2M" — must render
  // verbatim. Stripping every non-digit merged "99.9%" into "999%" (issue #533).
  const rawNumber = statNumber(stat);
  const match = String(rawNumber).match(/^(\D*)(\d[\d,]*)(\D*)$/);
  const parsed = match ? parseInt(match[2].replace(/,/g, ''), 10) : NaN;
  const isNumeric = match != null && !isNaN(parsed) && parsed > 0;
  const prefix = match?.[1] ?? '';
  const suffix = match?.[3] ?? '';

  const counted = useCountUp(isNumeric ? parsed : 0, animated && isNumeric && isVisible);
  const displayed = animated && isNumeric ? `${prefix}${counted}${suffix}` : rawNumber;

  return (
    <div ref={containerRef} className="text-center">
      {stat.icon && ICON_MAP[stat.icon] && (
        <div className="inline-flex items-center justify-center text-sage-600 mb-3" aria-hidden="true">
          {ICON_MAP[stat.icon]}
        </div>
      )}
      <p className="font-display text-4xl font-bold text-sage-900">
        {displayed}
      </p>
      {stat.label && (
        <p className="mt-1 text-sm text-sage-500">{stat.label}</p>
      )}
    </div>
  );
}

export default function StatsCounter({ section }: StatsCounterProps) {
  const stats: any[] = section.content?.stats ?? [];
  const animated: boolean = section.content?.animated ?? false;
  const headingId = `stats-heading-${section.id}`;

  if (stats.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Header */}
        {(section.title || section.subtitle) && (
          <div className="text-center mb-12">
            {section.title && (
              <h2
                id={headingId}
                className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
              >
                {section.title}
              </h2>
            )}
            {section.subtitle && (
              <p className="mt-2 text-sage-600 max-w-xl mx-auto">
                {section.subtitle}
              </p>
            )}
            <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
              <div className="h-px w-12 bg-sage-300" />
              <Sparkles className="h-4 w-4 text-sage-400" />
              <div className="h-px w-12 bg-sage-300" />
            </div>
          </div>
        )}

        {!section.title && (
          <h2 id={headingId} className="sr-only">Key Statistics</h2>
        )}

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
          {stats.map((stat: any, i: number) => (
            <StatItem key={i} stat={stat} animated={animated} />
          ))}
        </div>
      </div>
    </section>
  );
}
